// Vendor Program v2 PR 3 — VFI Accounting formal outputs: Past income
// (collected by month × vendor), Receivables aging, and Outstanding —
// sortable tables with age-bucket chips and client-side CSV export
// (reuses the deals CSV helper so exports match the on-screen view).

import { formatCalendarDate, formatPeriodMonth } from "@/design-system/lib/format";
import { useMemo, useState } from "react";
import {
  Button,
  Card,
  EmptyState,
  FilterChips,
  Skeleton,
  Table,
  Tabs,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from "@/design-system/components/ui";
import { Download } from "lucide-react";
import type { UseQueryResult } from "@tanstack/react-query";
import { useDateFormatter } from "@/utils/datetime";
import {
  useVfiInTransit,
  useVfiOutstanding,
  useVfiPastIncome,
  useVfiReceivablesAging,
} from "@/hooks/portfolioAdmin/useVendorBilling";
import type { VfiAgingRowDto, VfiPastIncomeRowDto } from "@/api/portfolioAdmin/types";
import { buildCsv, downloadCsvFile, type ColumnSpec } from "@/components/deals/csv";
import {
  fmtMoney,
  INVOICE_STATUS_LABEL,
} from "../billing/vendorBillingFormat";

export type AccountingTabId =
  | "past-income"
  | "aging"
  | "in-transit"
  | "outstanding";

// "Outstanding" was overdue-only, which reads as the whole receivable balance
// and gave the module a third meaning of "open" alongside the other two. It is
// labelled for what it returns. "In transit" is the settling-payment slice
// carved out of the aging report, surfaced so the money stays on screen.
const TABS: { id: AccountingTabId; label: string }[] = [
  { id: "past-income", label: "Past income" },
  { id: "aging", label: "Open receivables" },
  { id: "in-transit", label: "In transit" },
  { id: "outstanding", label: "Overdue" },
];

interface AccountingReportsSectionProps {
  activeTab: AccountingTabId;
  onTabChange: (tab: AccountingTabId) => void;
}

export function AccountingReportsSection({
  activeTab,
  onTabChange,
}: AccountingReportsSectionProps) {
  const pastIncomeQuery = useVfiPastIncome({ enabled: activeTab === "past-income" });
  const agingQuery = useVfiReceivablesAging({ enabled: activeTab === "aging" });
  const inTransitQuery = useVfiInTransit({ enabled: activeTab === "in-transit" });
  const outstandingQuery = useVfiOutstanding({ enabled: activeTab === "outstanding" });

  return (
    <Card data-testid="vfi-accounting-reports">
      <h3 className="font-display font-bold text-h3 m-0 mb-space-3">
        Accounting reports
      </h3>
      <Tabs
        tabs={TABS}
        activeId={activeTab}
        onChange={(id) => onTabChange(id as AccountingTabId)}
        ariaLabel="Accounting reports"
      >
        {activeTab === "past-income" && (
          <PastIncomeTable query={pastIncomeQuery} />
        )}
        {activeTab === "aging" && (
          <AgingTable
            query={agingQuery}
            csvFilename="vfi-receivables-aging.csv"
            testIdPrefix="vfi-aging"
          />
        )}
        {activeTab === "in-transit" && (
          <AgingTable
            query={inTransitQuery}
            csvFilename="vfi-in-transit.csv"
            testIdPrefix="vfi-in-transit"
          />
        )}
        {activeTab === "outstanding" && (
          <AgingTable
            query={outstandingQuery}
            csvFilename="vfi-overdue.csv"
            testIdPrefix="vfi-outstanding"
          />
        )}
      </Tabs>
    </Card>
  );
}

// ── shared bits ───────────────────────────────────────────────────────────

type SortDir = "asc" | "desc";

function SortableTH({
  label,
  field,
  align = "left",
  sortField,
  sortDir,
  onSort,
}: {
  label: string;
  field: string;
  align?: "left" | "right";
  sortField: string;
  sortDir: SortDir;
  onSort: (field: string) => void;
}) {
  const active = sortField === field;
  return (
    <TH
      className={`text-white-pure ${align === "right" ? "text-right" : ""}`}
      aria-sort={active ? (sortDir === "asc" ? "ascending" : "descending") : undefined}
    >
      <button
        type="button"
        onClick={() => onSort(field)}
        className="uppercase tracking-label font-display font-semibold text-gold hover:text-white-pure"
        data-testid={`sort-${field}`}
      >
        {label}
        {active ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
      </button>
    </TH>
  );
}

function compare(a: unknown, b: unknown): number {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b));
}

function useSorted<T>(
  rows: T[],
  sortField: string,
  sortDir: SortDir,
  pick: (row: T, field: string) => unknown,
): T[] {
  return useMemo(() => {
    const sorted = [...rows].sort((x, y) => compare(pick(x, sortField), pick(y, sortField)));
    return sortDir === "asc" ? sorted : sorted.reverse();
  }, [rows, sortField, sortDir, pick]);
}

function QueryStates({
  query,
  testIdPrefix,
  emptyTitle,
}: {
  query: UseQueryResult<unknown[]>;
  testIdPrefix: string;
  emptyTitle: string;
}) {
  if (query.isLoading) {
    return (
      <div className="flex flex-col gap-space-2" data-testid={`${testIdPrefix}-loading`}>
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }
  if (query.isError) {
    return (
      <div data-testid={`${testIdPrefix}-error`}>
        <p className="text-small text-status-error m-0" role="alert">
          Could not load this report.
        </p>
        <Button
          variant="secondary"
          size="sm"
          className="mt-space-2"
          onClick={() => void query.refetch()}
        >
          Retry
        </Button>
      </div>
    );
  }
  return (
    <div data-testid={`${testIdPrefix}-empty`}>
      <EmptyState variant="empty" title={emptyTitle} />
    </div>
  );
}

function ExportButton<T>({
  filename,
  rows,
  columns,
  testId,
}: {
  filename: string;
  rows: T[];
  columns: ColumnSpec<T>[];
  testId: string;
}) {
  return (
    <Button
      variant="secondary"
      size="sm"
      onClick={() => downloadCsvFile(filename, buildCsv(rows, columns))}
      data-testid={testId}
    >
      <Download size={14} aria-hidden /> Export CSV
    </Button>
  );
}

// ── Past income ───────────────────────────────────────────────────────────

function PastIncomeTable({ query }: { query: UseQueryResult<VfiPastIncomeRowDto[]> }) {
  const [sortField, setSortField] = useState("month");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const onSort = (field: string) => {
    if (field === sortField) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const pick = (r: VfiPastIncomeRowDto, field: string): unknown =>
    r[field as keyof VfiPastIncomeRowDto];
  const rows = useSorted(query.data ?? [], sortField, sortDir, pick);

  // Period boundaries are wall-calendar months stored at UTC midnight —
  // rendering them through the tz-aware formatter shifted June into May.
  const month = (iso: string) => formatPeriodMonth(iso, "short");

  if (!query.isSuccess || rows.length === 0) {
    return (
      <QueryStates
        query={query}
        testIdPrefix="vfi-past-income"
        emptyTitle="No collections yet"
      />
    );
  }

  const csvColumns: ColumnSpec<VfiPastIncomeRowDto>[] = [
    { header: "Month", value: (r) => month(r.month) },
    { header: "Vendor", value: (r) => r.vendorName },
    { header: "Collected", value: (r) => r.collected },
    { header: "Invoices", value: (r) => r.invoiceCount },
  ];

  return (
    <div className="flex flex-col gap-space-2" data-testid="vfi-past-income">
      <div className="flex justify-end">
        <ExportButton
          filename="vfi-past-income.csv"
          rows={rows}
          columns={csvColumns}
          testId="vfi-past-income-export"
        />
      </div>
      <Table data-testid="vfi-past-income-table">
        <THead>
          <TR>
            <SortableTH label="Month" field="month" sortField={sortField} sortDir={sortDir} onSort={onSort} />
            <SortableTH label="Vendor" field="vendorName" sortField={sortField} sortDir={sortDir} onSort={onSort} />
            <SortableTH label="Collected" field="collected" align="right" sortField={sortField} sortDir={sortDir} onSort={onSort} />
            <SortableTH label="Invoices" field="invoiceCount" align="right" sortField={sortField} sortDir={sortDir} onSort={onSort} />
          </TR>
        </THead>
        <TBody>
          {rows.map((r) => (
            <TR key={`${r.month}-${r.vendorId}`}>
              <TD className="whitespace-nowrap">{month(r.month)}</TD>
              <TD>{r.vendorName}</TD>
              <TD className="text-right tabular-nums">{fmtMoney(r.collected)}</TD>
              <TD className="text-right tabular-nums">{r.invoiceCount}</TD>
            </TR>
          ))}
        </TBody>
      </Table>
    </div>
  );
}

// ── Aging / Outstanding (same row shape, 30/60/90 bucket chips) ──────────

export function agingBucket(ageDays: number): "0-29" | "30-59" | "60-89" | "90+" {
  if (ageDays < 30) return "0-29";
  if (ageDays < 60) return "30-59";
  if (ageDays < 90) return "60-89";
  return "90+";
}

const BUCKETS = ["0-29", "30-59", "60-89", "90+"] as const;

function AgingTable({
  query,
  csvFilename,
  testIdPrefix,
}: {
  query: UseQueryResult<VfiAgingRowDto[]>;
  csvFilename: string;
  testIdPrefix: string;
}) {
  const { date } = useDateFormatter();
  const [bucket, setBucket] = useState<string>("all");
  const [sortField, setSortField] = useState("ageDays");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const onSort = (field: string) => {
    if (field === sortField) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const all = useMemo(() => query.data ?? [], [query.data]);
  const filtered = useMemo(
    () => (bucket === "all" ? all : all.filter((r) => agingBucket(r.ageDays) === bucket)),
    [all, bucket],
  );
  const pick = (r: VfiAgingRowDto, field: string): unknown =>
    r[field as keyof VfiAgingRowDto];
  const rows = useSorted(filtered, sortField, sortDir, pick);

  if (!query.isSuccess || all.length === 0) {
    return (
      <QueryStates
        query={query}
        testIdPrefix={testIdPrefix}
        emptyTitle="Nothing outstanding"
      />
    );
  }

  const chips = [
    { id: "all", label: "All", count: all.length },
    ...BUCKETS.map((b) => ({
      id: b,
      label: `${b} days`,
      count: all.filter((r) => agingBucket(r.ageDays) === b).length,
    })),
  ];

  const csvColumns: ColumnSpec<VfiAgingRowDto>[] = [
    { header: "Invoice #", value: (r) => r.invoiceNumber },
    { header: "Vendor", value: (r) => r.vendorName },
    { header: "Loan", value: (r) => r.loanNumber },
    { header: "Status", value: (r) => INVOICE_STATUS_LABEL[r.status] },
    { header: "Amount", value: (r) => r.amount },
    { header: "Issued", value: (r) => (r.issuedAt ? date(r.issuedAt) : "") },
    { header: "Due", value: (r) => (r.dueDate ? formatCalendarDate(r.dueDate) : "") },
    { header: "Age (days)", value: (r) => r.ageDays },
  ];

  return (
    <div className="flex flex-col gap-space-2" data-testid={testIdPrefix}>
      <div className="flex items-center justify-between gap-space-2 flex-wrap">
        <FilterChips
          chips={chips}
          activeId={bucket}
          onChange={setBucket}
          label="Age bucket"
        />
        <ExportButton
          filename={csvFilename}
          rows={rows}
          columns={csvColumns}
          testId={`${testIdPrefix}-export`}
        />
      </div>
      {rows.length === 0 ? (
        <EmptyState
          variant="noResults"
          title="No invoices in this bucket"
          description="Pick another age bucket to see rows."
        />
      ) : (
        <Table data-testid={`${testIdPrefix}-table`}>
          <THead>
            <TR>
              <SortableTH label="Invoice #" field="invoiceNumber" sortField={sortField} sortDir={sortDir} onSort={onSort} />
              <SortableTH label="Vendor" field="vendorName" sortField={sortField} sortDir={sortDir} onSort={onSort} />
              <SortableTH label="Loan" field="loanNumber" sortField={sortField} sortDir={sortDir} onSort={onSort} />
              <SortableTH label="Status" field="status" sortField={sortField} sortDir={sortDir} onSort={onSort} />
              <SortableTH label="Amount" field="amount" align="right" sortField={sortField} sortDir={sortDir} onSort={onSort} />
              <SortableTH label="Issued" field="issuedAt" sortField={sortField} sortDir={sortDir} onSort={onSort} />
              <SortableTH label="Due" field="dueDate" sortField={sortField} sortDir={sortDir} onSort={onSort} />
              <SortableTH label="Age" field="ageDays" align="right" sortField={sortField} sortDir={sortDir} onSort={onSort} />
            </TR>
          </THead>
          <TBody>
            {rows.map((r) => (
              <TR key={r.id} data-testid={`${testIdPrefix}-row-${r.id}`}>
                <TD className="whitespace-nowrap">{r.invoiceNumber}</TD>
                <TD>{r.vendorName}</TD>
                <TD>{r.loanNumber}</TD>
                <TD>{INVOICE_STATUS_LABEL[r.status]}</TD>
                <TD className="text-right tabular-nums">{fmtMoney(r.amount)}</TD>
                <TD className="whitespace-nowrap">{r.issuedAt ? date(r.issuedAt) : "—"}</TD>
                <TD className="whitespace-nowrap">{r.dueDate ? formatCalendarDate(r.dueDate) : "—"}</TD>
                <TD className="text-right tabular-nums">{r.ageDays}</TD>
              </TR>
            ))}
          </TBody>
        </Table>
      )}
    </div>
  );
}
